import {
  SESSION_HEADER,
  type ApiErrorBody,
  type ClaimDraftRequest,
  type ErrorCode,
  type LookupRequest,
  type LookupResponse,
  type OtpResendRequest,
  type OtpResendResponse,
  type OtpVerifyRequest,
  type OtpVerifyResponse,
  type PreviewResponse,
  type SubmitClaimRequest,
  type SubmitClaimResponse,
  type UploadResponse,
} from '@nvd/claim-shared';
import type { ResolvedConfig } from '@nvd/claim-shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly details?: unknown
  ) {
    super(message);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  json?: unknown;
  form?: FormData;
}

export function createApi(apiBase: string) {
  let session: string | null = null;

  async function request<T>(path: string, { method = 'GET', json, form }: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (session) headers[SESSION_HEADER] = session;
    if (json !== undefined) headers['Content-Type'] = 'application/json';

    let response: Response;
    try {
      response = await fetch(`${apiBase}${path}`, {
        method,
        headers,
        body: form ?? (json !== undefined ? JSON.stringify(json) : undefined),
        credentials: 'same-origin',
      });
    } catch {
      throw new ApiError(0, 'UPSTREAM_ERROR', 'Network error. Check your connection and try again.');
    }

    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const error = body as Partial<ApiErrorBody> | null;
      throw new ApiError(
        response.status,
        error?.code ?? 'INTERNAL_ERROR',
        error?.message ?? 'Something went wrong. Please try again.',
        error?.details
      );
    }
    return body as T;
  }

  return {
    setSession(token: string | null) {
      session = token;
    },
    config: () => request<ResolvedConfig>('/config'),
    lookup: (body: LookupRequest) => request<LookupResponse>('/lookup', { method: 'POST', json: body }),
    verifyOtp: (body: OtpVerifyRequest) => request<OtpVerifyResponse>('/otp/verify', { method: 'POST', json: body }),
    resendOtp: (body: OtpResendRequest) => request<OtpResendResponse>('/otp/resend', { method: 'POST', json: body }),
    upload: (file: Blob, name: string) => {
      const form = new FormData();
      form.append('file', file, name);
      return request<UploadResponse>('/uploads', { method: 'POST', form });
    },
    preview: (body: ClaimDraftRequest) => request<PreviewResponse>('/claims/preview', { method: 'POST', json: body }),
    submit: (body: SubmitClaimRequest) => request<SubmitClaimResponse>('/claims', { method: 'POST', json: body }),
  };
}

export type Api = ReturnType<typeof createApi>;
